import { useCallback, useEffect, useState } from "react";

import { useT } from "../../../shared/i18n";
import {
  Badge,
  Banner,
  Button,
  Card,
  controlClassName,
  ListRow,
  Skeleton,
} from "../../../shared/ui";
import {
  InspectorDenied,
  type FeedbackDetail,
  type FeedbackItem,
  type InspectorApi,
} from "./inspectorApi";
import { ErrorRetry } from "../ErrorRetry";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import { RedactedText } from "./RedactedText";

const STATUSES = ["new", "reviewed", "exported", "discarded"];

type Phase = "loading" | "ready" | "failed" | "denied";

/**
 * Operator queue of owner feedback (ADR 0216). Read-only except the status;
 * shows a permission message without `assistant.feedback.review`.
 */
export function FeedbackQueue({ api }: { api: InspectorApi }) {
  const t = useT();
  const reportExpired = useReportSessionExpired();
  const [status, setStatus] = useState("new");
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [phase, setPhase] = useState<Phase>("loading");
  const [detail, setDetail] = useState<FeedbackDetail | null>(null);
  const [failed, setFailed] = useState(false);

  const apply = useCallback(
    (request: Promise<FeedbackItem[]>) => {
      request
        .then((next) => {
          setItems(next);
          setPhase("ready");
        })
        .catch((error: unknown) => {
          if (isSessionExpired(error)) reportExpired();
          setPhase(error instanceof InspectorDenied ? "denied" : "failed");
        });
    },
    [reportExpired],
  );

  useEffect(() => {
    apply(api.listFeedback("new"));
  }, [api, apply]);

  const load = (filter: string) => {
    setPhase("loading");
    apply(api.listFeedback(filter));
  };

  const open = async (id: string) => {
    setFailed(false);
    try {
      setDetail(await api.feedbackDetail(id));
    } catch (error: unknown) {
      if (isSessionExpired(error)) reportExpired();
      setFailed(true);
    }
  };

  const move = async (id: string, next: string) => {
    setFailed(false);
    try {
      await api.setFeedbackStatus(id, next);
      setDetail(null);
      load(status);
    } catch (error: unknown) {
      if (isSessionExpired(error)) reportExpired();
      setFailed(true);
    }
  };

  if (phase === "denied") {
    return (
      <section
        aria-label={t("admin.inspector.queue.title")}
        className="flex flex-col gap-3"
      >
        <h2 className="text-title text-fg">
          {t("admin.inspector.queue.title")}
        </h2>
        <PermissionDenied message={t("admin.denied.feedback")} />
      </section>
    );
  }

  return (
    <section
      aria-label={t("admin.inspector.queue.title")}
      className="flex flex-col gap-3"
    >
      <h2 className="text-title text-fg">{t("admin.inspector.queue.title")}</h2>
      <label className="flex flex-col gap-1 text-footnote text-fg-muted">
        {t("admin.inspector.queue.status")}
        <select
          className={controlClassName}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setDetail(null);
            load(event.target.value);
          }}
        >
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      {phase === "failed" && (
        <ErrorRetry
          message={t("admin.inspector.failed")}
          onRetry={() => {
            load(status);
          }}
        />
      )}
      {phase !== "failed" && failed && (
        <Banner tone="error">{t("admin.inspector.failed")}</Banner>
      )}
      {phase === "loading" && items.length === 0 && (
        <div aria-busy="true">
          <Skeleton height={48} />
          <span className="sr-only">{t("admin.inspector.queue.loading")}</span>
        </div>
      )}
      {phase === "ready" && items.length === 0 && (
        <p className="text-body text-fg-muted">{t("admin.inspector.empty")}</p>
      )}
      {items.length > 0 && (
        <Card flush>
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                <ListRow
                  title={`${item.verdict} | ${item.reason ?? "-"} | ${item.intent ?? "-"}`}
                  subtitle={new Date(item.created_at).toLocaleString()}
                  trailing={
                    <Button
                      variant="tinted"
                      size="sm"
                      onClick={() => {
                        void open(item.id);
                      }}
                    >
                      {t("admin.inspector.open")}
                    </Button>
                  }
                />
              </li>
            ))}
          </ul>
        </Card>
      )}
      {detail !== null && (
        <Card title={t("admin.inspector.snapshot")}>
          <div className="mb-2 flex flex-wrap gap-2">
            <Badge tone="neutral">{detail.status}</Badge>
            <Badge tone="info">{detail.outcome_kind ?? "-"}</Badge>
          </div>
          {detail.snapshot !== null &&
          typeof detail.snapshot.owner_text === "string" ? (
            <div className="flex flex-col gap-2">
              <RedactedText text={detail.snapshot.owner_text} />
              {typeof detail.snapshot.reply === "string" && (
                <RedactedText text={detail.snapshot.reply} />
              )}
            </div>
          ) : (
            <p className="text-body text-fg-muted">
              {t("admin.inspector.queue.noSnapshot")}
            </p>
          )}
          {detail.status === "new" || detail.status === "reviewed" ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {detail.status === "new" && (
                <Button
                  onClick={() => {
                    void move(detail.id, "reviewed");
                  }}
                >
                  {t("admin.inspector.queue.reviewed")}
                </Button>
              )}
              <Button
                variant="dangerSoft"
                onClick={() => {
                  void move(detail.id, "discarded");
                }}
              >
                {t("admin.inspector.queue.discarded")}
              </Button>
            </div>
          ) : null}
        </Card>
      )}
    </section>
  );
}
